// SAB-587: the pure landing-route decision. No IO imports on purpose, so it is
// trivially unit-testable and the resolver in landingRoute.ts is the only place
// that touches the database.
//
// The chain decides the ROUTE (which sport shell), never a specific NFL league.
// Choosing the NFL league is delegated to NFLLeagueContext at /nfl, so
// this stays consistent with SAB-577 (NFL active-league persistence) instead of
// racing it with an independent ORDER BY. A landing fix that picked its own NFL
// league could disagree with SAB-577 and produce a wrong-league landing, which
// on a money app is worse than the wrong-sport landing this ticket fixes.

export const NFL_HOME = '/nfl';
export const MLB_HOME = '/beta';
export const NFL_CREATE = '/nfl/create';
// SAB-642: the cold-start CHOOSER (join-with-a-code / browse / create), not the
// create-only wizard. Rule 4 lands here so a membership-less user is never forced to
// commission a league to get past onboarding — invite-based growth makes joining the
// majority path, and it must be reachable without backing out of create.
export const NFL_START = '/nfl/start';

export interface LandingSignals {
  hasActiveNfl: boolean; // MEMBER of an nfl_league with status 'active'
  hasInSeasonMlb: boolean; // team in an mlb league with status 'in_season'
  hasAnyNfl: boolean; // MEMBER of any nfl_league (incl completed)
}

/**
 * SAB-587 landing fallback chain. Order is load-bearing:
 *   1. member of an ACTIVE NFL league       -> NFL shell
 *   2. team in an IN_SEASON MLB league       -> MLB shell   (above completed NFL
 *                                            so a mid-season player lands on the
 *                                            game they are actually playing, not
 *                                            a finished NFL beta league)
 *   3. member of ANY NFL league incl completed -> NFL shell (keeps the App Review
 *                                            demo account, and anyone who joined
 *                                            but has not drafted a team yet, on
 *                                            their league instead of a create screen)
 *   4. no NFL membership at all             -> NFL cold-start chooser (join/browse/create)
 *
 * NFL rules are MEMBERSHIP-based (see landingRoute.ts): a user who is a member of
 * an NFL league must never be routed to create one, even in the window between
 * accepting an invite and drafting a team. Rule 2 stays team-based; MLB creates a
 * team at join and its in_season scoping retires the rule when seasons complete.
 */
export function pickLandingRoute(s: LandingSignals): string {
  if (s.hasActiveNfl) return NFL_HOME; // 1
  if (s.hasInSeasonMlb) return MLB_HOME; // 2
  if (s.hasAnyNfl) return NFL_HOME; // 3
  return NFL_START; // 4 — cold-start chooser, never forced create
}
